/*
 * IMPORTS
 */
import React from 'react' // Npm: react.js library.
import Moment from 'moment' // Npm: Moment.js library.
import PropTypes from 'prop-types' // Npm: Prop types for checking props type.
import _ from 'underscore' // Npm: Underscore.js library.
import { IoMdAdd } from 'react-icons/io' // Npm: React icons.
import { connect } from 'react-redux' // Npm: React redux.
import { useMutation, useQuery } from '@apollo/client' // Npm: Apollo client.
import { DataGrid } from '@mui/x-data-grid' // Npm: Material UI Data Grid.
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Spinner,
  Text,
  Tooltip,
  useBreakpointValue,
  useDisclosure
} from '@chakra-ui/react' // Npm: Chakra UI components.


/*
 * PACKAGES
 */
import TableSpinner from 'components/TableSpinner'
import DownloadToExcel from 'components/DownloadToExcel'
import Modal from 'components/Modal'
import AccountLogout from 'components/AccountLogout'
import AccountUpsert from 'components/AccountUpsert'
import AccountDelete from 'components/AccountDelete'
import TwoFactorAuthentication from 'components/2FA'
import AccountDropDownOptions from 'components/AccountDropDownOptions'
import { MemoizedInput, MemoizedSelect } from 'components/MemoizedInput'


/*
 * GRAPHS
 */
import AccountSettingMutation from './__mutation__/index.account.setting.mutation'
import AccountTwoFactorAuthenticationDeleteMutation from './__mutation__/index.account.twoFactorAuthenticationDelete.mutation'
import AccountIsBlockedWithEmailOrAccountIdMutation from './__mutation__/index.account.isBlockedWithEmailOrAccountId.mutation'
import AccountReadQuery from './__query__/index.account.read.query'


/*
 * OBJECTS
 */
const Index = ({ PassOnUpdate }) => {
  // Const assignment.
  const _successFlags = Object.React.App.enums.GRAPHQL_SUCCESSFUL_QUERY_FLAGS.enums.map(i => i.key)

  // Hook assignment.
  const [paginationModel, setPaginationModel] = React.useState({
    'pageSize': 10,
    'page': 0
  })
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({ 'id': false })
  const [filter, setFilter] = React.useState({ 'items': [] })
  const _setFilterDebounced = React.useMemo(() => _.debounce(setFilter, 500), [])
  const [selectedAccountType, setSelectedAccountType] = React.useState('EMPLOYEE')
  const [searchQuery, setSearchQuery] = React.useState('')
  const [isBlockInProgress, setIsBlockInProgress] = React.useState([])
  const [is2FAInProgress, setIs2FAInProgress] = React.useState([])

  const { 'onOpen': on2FAOpen, 'isOpen': is2FAOpen, 'onClose': on2FAClose } = useDisclosure()
  const { 'onOpen': onAccountLogoutOpen, 'isOpen': isAccountLogoutOpen, 'onClose': onAccountLogoutClose } = useDisclosure()
  const { 'onOpen': onAccountUpdateOpen, 'isOpen': isAccountUpdateOpen, 'onClose': onAccountUpdateClose } = useDisclosure()
  const { 'onOpen': onAccountDeleteOpen, 'isOpen': isAccountDeleteOpen, 'onClose': onAccountDeleteClose } = useDisclosure()
  const { 'onOpen': onAccountCreateOpen, 'isOpen': isAccountCreateOpen, 'onClose': onAccountCreateClose } = useDisclosure()

  const [MutationAccountSetting] = useMutation(AccountSettingMutation)
  const [MutationAccountTwoFactorAuthenticationDelete] = useMutation(AccountTwoFactorAuthenticationDeleteMutation)
  const [MutationAccountIsBlockedWithEmailOrAccountId] = useMutation(AccountIsBlockedWithEmailOrAccountIdMutation)

  const _onSearchInputChange = React.useRef(
    _.debounce((e) => setSearchQuery(e.target.value), 800)
  ).current

  const _onAccountTypeChange = React.useRef(
    _.debounce((e) => setSelectedAccountType(_.isEmpty(e.target.value) ? void 0 : e.target.value), 800)
  ).current

  React.useEffect(() => {
    return () => {
      _onSearchInputChange.cancel?.()
      _onAccountTypeChange.cancel?.()
    }
  }, [])

  // Data assignment.
  const _isFilterApplied = filter?.items?.some(i => i.value != void 0 && i.value !== '')
  const _appliedFilter = _isFilterApplied ? filter : void 0

  const _QueryAccountRead = useQuery(AccountReadQuery, {
    'variables': {
      'accountType': selectedAccountType,
      'filter': _appliedFilter,
      'skip': paginationModel.pageSize * paginationModel.page,
      'take': paginationModel.pageSize,
      'search': searchQuery
    },
    'fetchPolicy': Object.React.App.fetchPolicy,
    'pollInterval': Object.React.App.pollInterval
  })

  const _isFirstLoadCompleted = React.useRef(false)
  const _tableHeaderHeightRef = React.useRef(0)
  const _isCurrentViewMobile = useBreakpointValue({
    'base': false,
    'md': false,
    'lg': false,
    'xl': false,
    'sm': true,
    'xs': true
  })

  // Data assignment.
  if (!_QueryAccountRead.loading && 0 < _QueryAccountRead?.data?.AccountRead?.length) _isFirstLoadCompleted.current = true
  if (0 === _QueryAccountRead?.data?.AccountRead?.length || !_.every(_.pluck(_QueryAccountRead?.data?.AccountRead, 'status'), j => _successFlags.includes(j))) _isFirstLoadCompleted.current = false

  // Object assignment.
  const _onBanToggle = async (e, id) => {
    // Update block status.
    setIsBlockInProgress(i => [...i, id])

    // Update block status.
    await MutationAccountIsBlockedWithEmailOrAccountId({ 'variables': { 'accountId': id, 'isBlocked': e.nativeEvent?.target?.checked } })

    // Refetch account details.
    await _QueryAccountRead.refetch()

    // Update block status.
    setIsBlockInProgress(r => _.without(r, id))
  }
  const _on2FAToggle = async (e, id, email) => {
    // Const assignment.
    const _isChecked = e.nativeEvent?.target?.checked

    // Update passOn with email.
    PassOnUpdate({ email, 'accountId': id })

    // If 2FA is enabled then open 2FA modal.
    if (_isChecked) return on2FAOpen()

    // Update 2FA status.
    setIs2FAInProgress(i => [...i, id])

    // Remove 2Fa.
    await MutationAccountTwoFactorAuthenticationDelete({ 'variables': { email } })

    // Update 2FA status.
    await MutationAccountSetting({ 'variables': { 'accountId': id, 'is2FAEnabled': _isChecked } })

    // Refetch account details.
    await _QueryAccountRead.refetch()

    // Update 2FA status.
    return setIs2FAInProgress(r => _.without(r, id))
  }

  // Columns definition
  const columns = React.useMemo(() => [
    {
      'field': 'sno',
      'headerName': 'S.NO',
      'align': 'center',
      'headerAlign': 'center',
      'width': 80,
      'sortable': false,
      'filterable': false,
      'disableColumnMenu': true
    },
    {
      'field': 'id',
      'headerName': 'ID',
      'align': 'center',
      'editable': false,
      'sortable': false,
      'filterable': true,
      'headerAlign': 'center',
      'disableColumnMenu': true
    },
    {
      'field': 'displayName',
      'headerName': 'Account Name',
      'description': 'Name of account.',
      'editable': false,
      'sortable': true,
      'filterable': true,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 150
    },
    {
      'field': 'email',
      'headerName': 'Email',
      'description': 'Email of account.',
      'editable': false,
      'sortable': true,
      'filterable': true,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 280
    },
    {
      'field': 'accountType',
      'headerName': 'Account Type',
      'description': 'Type of account.',
      'editable': false,
      'sortable': false,
      'filterable': true,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 130,
      renderCell: (params) => (
        <Flex
          w="100%"
          h="100%"
          align="center"
          justify="center"
          overflow="hidden"
        >
          <MemoizedInput
            value={params.value}
            borderRadius="10px"
            fontSize="15px"
            fontWeight="500"
            bg="gray.100"
            w="100%"
            disabled
            border="1px solid rgba(216, 227, 252, 1)"
          />
        </Flex>
      )
    },
    {
      'field': 'createdAt',
      'headerName': 'Created At',
      'description': 'Creation date of account.',
      'editable': false,
      'sortable': false,
      'filterable': true,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 120,
      'renderCell': (params) => params.value ? Moment(params.value).format('YYYY-MM-DD') : '-'
    },
    {
      'field': 'updatedAt',
      'headerName': 'Updated At',
      'description': 'Last update date of account.',
      'editable': false,
      'sortable': false,
      'filterable': true,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 120,
      'renderCell': (params) => params.value ? Moment(params.value).format('YYYY-MM-DD') : '-'
    },
    {
      field: 'ban',
      headerName: 'Ban',
      description: 'Ban status of account.',
      editable: false,
      sortable: false,
      filterable: false,
      align: 'center',
      headerAlign: 'center',
      width: 100,
      renderCell: ({ row }) => (
        <Flex width="100%" height="100%" justify="center" align="center">
          {isBlockInProgress.includes(row.id) ? (
            <Spinner color='#c1a9f4' size='sm' />
          ) : (
            <Checkbox
              bg='gray.100'
              isChecked={row.isBlocked}
              onChange={e => _onBanToggle(e, row.id)}
            />
          )}
        </Flex>
      )
    },
    {
      field: '2fa',
      headerName: '2FA',
      description: '2FA status of account.',
      editable: false,
      sortable: false,
      filterable: false,
      align: 'center',
      headerAlign: 'center',
      width: 100,
      renderCell: ({ row }) => (
        <Flex width="100%" height="100%" justify="center" align="center">
          {is2FAInProgress.includes(row.id) ? (
            <Spinner color='#c1a9f4' size='sm' />
          ) : (
            <Checkbox
              bg='gray.100'
              isChecked={row.is2FAEnabled}
              onChange={e => _on2FAToggle(e, row.id, row.email)}
            />
          )}
        </Flex>
      )
    },

    {
      'field': 'action',
      'headerName': 'Actions',
      'description': 'Actions available for the account.',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'disableColumnMenu': true,
      'width': 100,
      'renderCell': ({ row }) => (
        <AccountDropDownOptions
          accountId={row.id}
          accountType={row.accountType}
          onAccountUpdate={onAccountUpdateOpen}
          onAccountLogout={onAccountLogoutOpen}
          onAccountDelete={onAccountDeleteOpen}
        />
      )
    }
  ], [isBlockInProgress, is2FAInProgress])

  // Rows data
  const rows = React.useMemo(() => _.compact(_QueryAccountRead?.data?.AccountRead?.map((r, index) =>
    _successFlags.includes(r.status) ? ({
      ...r,
      'id': r?.id || `account-${index}-${Date.now()}`,
      'sno': `${(index + (paginationModel.page * paginationModel.pageSize)) + 1}.`,
      'displayName': r?.displayName || '-',
      'email': r?.email || '-',
      'accountType': r?.accountType || '-',
      'createdAt': r?.createdAt ? Moment(r.createdAt).format('YYYY-MM-DD') : '-',
      'updatedAt': r?.updatedAt ? Moment(r.updatedAt).format('YYYY-MM-DD') : '-',
      'isBlocked': r?.isBlocked || false,
      'is2FAEnabled': r?.is2FAEnabled || false
    }) : null
  )), [_QueryAccountRead?.data?.AccountRead, paginationModel.page, paginationModel.pageSize])

  // Return Component.
  return (
    <>
      <Flex className='account base'>
        <Flex
          display='flex'
          flex={1}
          flexDirection='column'
          position='relative'
          gap={_isCurrentViewMobile ? '12px' : '14px'}
          bg='white'
          minH={_isCurrentViewMobile ? '115vh' : '98vh'}
          maxH={_isCurrentViewMobile ? '115vh' : '98vh'}
          borderRadius='20px'
          p={_isCurrentViewMobile ? '12px' : '22px'}>
          <Flex pb='0px' justify='space-between' alignItems='center'>
            <Tooltip label='Create new account.' fontSize='sm'>
              <Button
                w='170px'
                color="rgb(92, 55, 255)"
                fontFamily="DM Sans"
                fontWeight="500"
                fontSize="16px"
                borderRadius="10px"
                onClick={onAccountCreateOpen}
                leftIcon={<IoMdAdd />}
                className='addButton'>
                New Account
              </Button>
            </Tooltip>
            <DownloadToExcel
              cellsData={_QueryAccountRead?.data?.AccountRead?.map((item, __index) =>
                _successFlags.includes(item.status) ? ({
                  'S.No.': `${(__index + (paginationModel.pageSize * paginationModel.page)) + 1}.`,
                  'Account Name': item.displayName ?? '-',
                  'Email': item.email ?? '-',
                  'Account Status': item.accountType,
                  'CreatedAt': Moment(item?.createdAt).format('YYYY-MM-DD'),
                  'UpdatedAt': Moment(item?.updatedAt).format('YYYY-MM-DD'),
                  'Ban': item.isBlocked ?? '-'
                }) : ({})
              )}
              headersData={[
                'S.No.',
                'Account Name',
                'Email',
                'Account Status',
                'CreatedAt',
                'UpdatedAt',
                'Ban'
              ].map(k => ({ 'key': k, 'label': k }))}
            />
          </Flex>

          {/*  FILTER SECTION  */}
          <Flex
            gap='12px'
            flexWrap='wrap'
            bg='white'
            p='16px'
            borderRadius='12px'
            border='1px solid #C5CFE8'
            alignItems='flex-start'
          >
            <Box
              minW={{ base: '100%', md: '150px' }}
              w={{ base: '100%', md: '200px' }}
            >
              <Text fontSize='14px' fontWeight={600} color='rgb(141, 151, 181)'>Email</Text>
              <MemoizedInput
                placeholder='Search by email'
                className='filter searchInput'
                defaultValue={searchQuery}
                onChange={_onSearchInputChange}
              />
            </Box>

            <Box
              minW={{ base: '100%', md: '150px' }}
              w={{ base: '100%', md: '200px' }}
            >
              <Text fontSize='14px' fontWeight={600} color='rgb(141, 151, 181)'>Account Type</Text>
              <MemoizedSelect
                name='accountType'
                placeholder='Select Account Type'
                className='filter searchInput'
                onChange={_onAccountTypeChange}
                options={Object.React.App.enums.ACCOUNT_TYPE.enums?.map(i => i?.key)}
                data={selectedAccountType}
              />
            </Box>
          </Flex>


          {/* MUI DataGrid */}
          <DataGrid
            key={`${paginationModel.page}-${paginationModel.pageSize}`}
            rows={rows}
            columns={columns}
            rowHeight={58}
            rowCount={_.pluck(_QueryAccountRead?.data?.AccountRead, '_totalCount')?.[0] || 0}
            paginationMode='server'
            filterMode='server'
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            onFilterModelChange={_setFilterDebounced}
            pageSizeOptions={[10, 30, 50, 100]}
            slots={{
              'loadingOverlay': () => <TableSpinner isLoading={true} isEmpty={false} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />,
              'noResultsOverlay': () => <TableSpinner isLoading={false} isEmpty={true} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />,
              'noRowsOverlay': () => <TableSpinner isLoading={false} isEmpty={true} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />
            }}
            disableRowSelectionOnClick
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={newModel => setColumnVisibilityModel(newModel)}
            loading={_QueryAccountRead?.loading}
          />
        </Flex>

        {/* Modals */}
        <Modal
          size='lg'
          title='Account Create'
          isOpen={isAccountCreateOpen}
          onClose={onAccountCreateClose}
          isCentered={false}>
          <AccountUpsert isCreateOnly={true} />
        </Modal>
        <Modal
          size='lg'
          title='Account Update'
          isOpen={isAccountUpdateOpen}
          onClose={onAccountUpdateClose}
          isCentered={false}>
          <AccountUpsert />
        </Modal>
        <Modal
          size='md'
          title='Account Logout'
          isOpen={isAccountLogoutOpen}
          onClose={onAccountLogoutClose}>
          <AccountLogout />
        </Modal>
        <Modal
          size='md'
          title='Account Delete'
          isOpen={isAccountDeleteOpen}
          onClose={onAccountDeleteClose}>
          <AccountDelete />
        </Modal>
        <Modal
          size='sm'
          title='2FA Enable/Disable'
          isOpen={is2FAOpen}
          onClose={on2FAClose}>
          <TwoFactorAuthentication />
        </Modal>
      </Flex>
    </>
  )
}


/*
 * PROPTYPES
 */
Index.propTypes = {
  'PassOnUpdate': PropTypes.func.isRequired
}


/*
 * REDUX
 */
const _MapDispatchToProps = __dispatch => ({
  'PassOnUpdate': u => __dispatch({ 'type': 'PASS_ON_UPDATE', 'PassOn': u })
})


/*
 * EXPORTS
 */
export default connect(void 0, _MapDispatchToProps)(Index)
